import * as THREE from 'three';
import { cycleColor } from '../color-cycle.js';

// Orchestrator registry entry (see visualizer-registry.js) - this layer is
// scored/selected as its own candidate ('dust') even though it has no
// Visualizer subclass of its own; adaptive-party.js composes it directly.

// Ces deux couches ne sont PAS rendues seules. adaptive-party.js les monte sur
// le rig de scene partage, le sol 60x60 et la camera de neon-pulse.js - son
// propre commentaire le dit : "same rig/floor/camera shape as neon-pulse.js's
// own setup". Ce qui arrive a l'ecran, ce sont donc les faisceaux de club de
// NeonPulse, avec ces particules dedans.
//
// Le PROFILE decrivait la COUCHE (poussiere lente, energie 0.20) et pas
// l'image. Les deux se contredisaient, et comme la fiche disait "calme",
// l'orchestrateur les choisissait justement pour du classique - d'ou des
// faisceaux de boite de nuit sur un nocturne, sans que NeonPulse ait jamais
// ete selectionne. Les valeurs ci-dessous decrivent ce qui est montre.
//
// Le `prefers` compte autant que les chiffres : sans lui, ces deux entrees
// n'avaient AUCUN axe de notation des que le bpm manquait (elles ne declaraient
// rien, contrairement aux visualiseurs calmes), tombaient a 0.5 comme tout le
// monde et ressortaient au hasard uniforme.
export const PROFILE = {
  id: 'dust',
  family: 'particles',
  energyLevel: 0.55,
  bpmMin: 90,
  bpmMax: 150,
  movement: 0.45,
  danceability: 0.60,
  tonality: 0.50,
  prefers: { dynamics: [0.0, 0.65], percussiveness: [0.35, 1.0] },
  enabled: true
};

// Floating dust/smoke motes - the particle layer NeonPulse (the main
// party mode) starts on. Extracted from what used to be the whole of
// neon-pulse.js so it can also be swapped in as one of several rotating
// layers under the same persistent rig/camera - see
// visualizers/neon-pulse.js and PARTY-mode's layer-cycling comment there.

const PARTICLE_COUNT = 420;
const FIELD_RADIUS = 7.5;
const FIELD_HEIGHT = 5.5;
const FALLBACK_COLORS = [0xff2fb0, 0x2fd8ff, 0xffb02f, 0x8f2fff, 0x2fff8f];
const CYCLE_SPEED = 1 / 33;
const BASE_SIZE = 0.075;
const BASE_OPACITY = 0.55;
// Gentler than the rig's flash (see beat-pulse.js) - these are meant to
// read as a soft ambient twinkle, not a strobe, so a smaller amount and a
// slower decay than the rig's.
const BEAT_PULSE_AMOUNT = 0.35;
const BEAT_PULSE_DECAY = 9; // one full pass through all detected colors every ~33s

// THREE.PointsMaterial with no map draws a hard-edged square sprite per
// point - looks like flying pixels, not dust. A small radial-gradient
// canvas texture as the sprite map gives each mote a soft round falloff
// instead, cheap (one 64x64 texture, drawn once and shared by the whole
// system) and a direct fix for the "too precise/sharp" look.
function createSoftDotTexture() {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.4, 'rgba(255,255,255,0.55)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(canvas);
}

export class DustLayer {
  init(scene) {
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    this._basePositions = new Float32Array(PARTICLE_COUNT * 3);
    this._driftSeed = new Float32Array(PARTICLE_COUNT);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * FIELD_RADIUS;
      const x = Math.cos(angle) * radius;
      const y = Math.random() * FIELD_HEIGHT;
      const z = Math.sin(angle) * radius;
      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
      this._basePositions[i * 3] = x;
      this._basePositions[i * 3 + 1] = y;
      this._basePositions[i * 3 + 2] = z;
      this._driftSeed[i] = Math.random() * Math.PI * 2;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({
      color: 0xffffff,
      map: createSoftDotTexture(),
      size: BASE_SIZE,
      transparent: true,
      opacity: BASE_OPACITY,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true
    });
    this._points = new THREE.Points(geometry, material);
    scene.add(this._points);

    this._time = 0;
    this._tmpColor = new THREE.Color();
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    if (!paused) this._time += deltaTime;

    // Gentle upward rise + horizontal sway, wrapping back to the bottom
    // once a mote drifts past the top of the field - plain per-particle
    // sine math, no physics/collision needed for "floating dust".
    const position = this._points.geometry.attributes.position;
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const seed = this._driftSeed[i];
      const bx = this._basePositions[i * 3];
      const bz = this._basePositions[i * 3 + 2];
      position.array[i * 3] = bx + Math.sin(this._time * 0.3 + seed) * 0.3;
      position.array[i * 3 + 1] = (this._time * 0.25 + seed) % FIELD_HEIGHT;
      position.array[i * 3 + 2] = bz + Math.cos(this._time * 0.25 + seed) * 0.3;
    }
    position.needsUpdate = true;

    const dominant = profile.artworkPalette && profile.artworkPalette.dominantColors;
    const colors = dominant && dominant.length >= 2 ? dominant : FALLBACK_COLORS;
    cycleColor(colors, this._time, CYCLE_SPEED, this._tmpColor);
    this._points.material.color.copy(this._tmpColor);

    // Beat-locked twinkle (see beat-pulse.js) - a gentle synchronized
    // brighten/grow on the beat, on top of the color cycling above.
    const pulse = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);
    this._points.material.size = BASE_SIZE * pulse;
    this._points.material.opacity = Math.min(1, BASE_OPACITY * pulse);
  }

  dispose(scene) {
    scene.remove(this._points);
    this._points.geometry.dispose();
    this._points.material.map.dispose();
    this._points.material.dispose();
  }
}
